import { useRouter } from "next/router"
import { useCallback, useMemo } from "react"
import {
  decodeQueryParam,
  encodeQueryParam,
  optionFromValue,
  useLocalFilter,
} from "shared-ts/hooks/useFilter"
import {
  FilterType,
  OptionT,
  TextFilterT,
} from "shared-ts/hooks/useFilter/types/filter.types"
import {
  UseFilterProps,
  UseFilterRes,
  UseWebFilterProps,
} from "shared-ts/hooks/useFilter/types/useFilter.types"

export { decodeQueryParam, encodeQueryParam }
export type { UseWebFilterProps }

export function useWebFilter<TValue>({
  persistInUrl = true,
  ...restProps
}: UseWebFilterProps<TValue>): UseFilterRes<TextFilterT> {
  const localRes = useLocalFilter(restProps)
  const urlRes = useUrlFilter({
    ...restProps,
  })

  return useMemo(() => {
    return persistInUrl === false ? localRes : urlRes
  }, [persistInUrl, localRes, urlRes])
}

export function useUrlFilter<TValue>({
  key,
  label,
  values,
  sort,
  keyExtractor,
  labelExtractor,
  contentExtractor,
  onChangeQuery,
  autoFilterOptions = true,
  allowExternalValues = false,
  onLoadMore,
}: UseFilterProps<TValue>): UseFilterRes<TextFilterT> {
  const router = useRouter()
  const queryParam = router.query[key]

  const normalizedQueryParam = useMemo<string[]>(() => {
    if (queryParam === undefined) {
      return []
    }
    return Array.isArray(queryParam) ? queryParam : [queryParam]
  }, [queryParam])

  const valuesByKey = useMemo<Record<string, TValue>>(() => {
    return values.reduce<Record<string, TValue>>((acc, value) => {
      acc[keyExtractor(value)] = value
      return acc
    }, {})
  }, [values, keyExtractor])

  const selectedOptions = useMemo<OptionT[]>(() => {
    return normalizedQueryParam.map((param) => {
      const decoded = decodeQueryParam(param)
      const value = valuesByKey[decoded.key]

      if (value) {
        return optionFromValue(
          value,
          keyExtractor,
          labelExtractor,
          contentExtractor
        )
      }

      return decoded
    })
  }, [
    normalizedQueryParam,
    keyExtractor,
    labelExtractor,
    contentExtractor,
    valuesByKey,
  ])

  const selectedOptionKeys = useMemo<string[]>(
    () => selectedOptions.map((o) => o.key),
    [selectedOptions]
  )

  const options = useMemo<OptionT[]>(() => {
    const sorted = sort ? [...values].sort(sort) : values
    return sorted.map((value) =>
      optionFromValue(value, keyExtractor, labelExtractor, contentExtractor)
    )
  }, [values, keyExtractor, labelExtractor, contentExtractor, sort])

  const onSelectOption = useCallback(
    (option: OptionT) => {
      const newQuery = { ...router.query }
      const encoded = encodeQueryParam(option, allowExternalValues)

      if (selectedOptionKeys.includes(option.key)) {
        const filtered = normalizedQueryParam.filter(
          (param) => decodeQueryParam(param).key !== option.key
        )
        if (filtered.length > 0) {
          newQuery[key] = filtered
        } else {
          delete newQuery[key]
        }
      } else {
        newQuery[key] = [...normalizedQueryParam, encoded]
      }

      router.push(
        {
          pathname: router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      )
    },
    [key, normalizedQueryParam, router, selectedOptionKeys, allowExternalValues]
  )

  const onClear = useCallback(() => {
    if (normalizedQueryParam.length === 0) return
    const newQuery = { ...router.query }
    delete newQuery[key]
    router.push({ pathname: router.pathname, query: newQuery }, undefined, {
      shallow: true,
    })
  }, [router, normalizedQueryParam, key])

  const filter: TextFilterT = {
    type: FilterType.Text,
    key,
    value: label,
    options,
    selectedOptions,
    onSelectOption,
    onClear,
    onChangeQuery,
    autoFilterOptions,
    onLoadMore,
  }

  return { filter, selectedKeys: selectedOptionKeys }
}
